import { MARKET_SYMBOLS, type FundSnapshot, type PublicSnapshot } from '../../core/types.ts';
import { pct, usd } from '../lib/format.ts';
import { Panel, Pill } from './ui.tsx';
import { sideTheme } from './FundCard.tsx';

/** Worst-case simultaneous stop-out across all three ladders, as a share of equity. */
function stopOutRisk(fund: FundSnapshot): number {
  return MARKET_SYMBOLS.reduce((sum, s) => {
    const m = fund.config.markets[s];
    return sum + fund.config.deployRatio * m.weight * m.leverage * m.stopPct;
  }, 0);
}

function grossExposure(fund: FundSnapshot): number {
  return MARKET_SYMBOLS.reduce((sum, s) => {
    const m = fund.config.markets[s];
    return sum + fund.config.deployRatio * m.weight * m.leverage;
  }, 0);
}

function FundBlock({ fund }: { fund: FundSnapshot }) {
  const t = sideTheme(fund.side);
  const deployable = fund.equityUsd * fund.config.deployRatio;

  return (
    // min-w-0 so this grid column can shrink below the table's min-width and let
    // the scroll wrapper below take over, rather than widening the page.
    <div className="min-w-0">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <img src={fund.logo} alt="" className="size-6 rounded-full" width={24} height={24} />
        <h3 className={`text-[13px] font-semibold ${t.text}`}>
          {fund.name} · {fund.side}
        </h3>
        <Pill tone="neutral" title="Gross notional if every ladder were full, as a multiple of fund equity">
          max {grossExposure(fund).toFixed(2)}x gross
        </Pill>
        <Pill
          tone="down"
          title="If all three ladders were full and every stop triggered at once, this is the share of fund equity lost"
        >
          {pct(stopOutRisk(fund), 1)} max drawdown
        </Pill>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-left">
          <thead>
            <tr className="text-[10px] tracking-wide text-dim uppercase">
              <th className="py-1.5 pr-3 font-medium">Market</th>
              <th className="px-2 py-1.5 text-right font-medium">Weight</th>
              <th className="px-2 py-1.5 text-right font-medium">Leverage</th>
              <th className="px-2 py-1.5 text-right font-medium">Target</th>
              <th className="px-2 py-1.5 text-right font-medium">Stop</th>
              <th className="px-2 py-1.5 text-right font-medium">Breakeven at</th>
              <th className="px-2 py-1.5 text-right font-medium">Ladder</th>
              <th className="px-2 py-1.5 text-right font-medium">Allocation</th>
            </tr>
          </thead>
          <tbody className="num text-[12px]">
            {MARKET_SYMBOLS.map((s) => {
              const m = fund.config.markets[s];
              return (
                <tr key={s} className="border-t border-edge/60">
                  <td className="py-1.5 pr-3 text-[12px] font-semibold">{s}</td>
                  <td className="px-2 py-1.5 text-right text-muted">{pct(m.weight, 0)}</td>
                  <td className={`px-2 py-1.5 text-right font-semibold ${t.text}`}>{m.leverage.toFixed(1)}x</td>
                  <td className="px-2 py-1.5 text-right text-up">+{pct(m.takeProfitPct, 1)}</td>
                  <td className="px-2 py-1.5 text-right text-down">-{pct(m.stopPct, 1)}</td>
                  <td className="px-2 py-1.5 text-right text-muted">+{pct(m.trailArmPct, 1)}</td>
                  <td className="px-2 py-1.5 text-right text-muted">
                    {m.maxLadderSteps}x every {pct(m.ladderSpacingPct, 1)}
                  </td>
                  <td className="px-2 py-1.5 text-right text-muted">{usd(deployable * m.weight, 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function StrategyTable({ snap }: { snap: PublicSnapshot | null }) {
  if (!snap) return null;

  return (
    <Panel
      title="The rules, in full"
      subtitle="Nothing here is discretionary. Target and stop percentages are moves in the price, not in P&L — multiply by leverage for the return on margin."
      right={<Pill tone="neutral">strategy v{snap.strategyVersion}</Pill>}
    >
      <div className="grid gap-6 xl:grid-cols-2">
        <FundBlock fund={snap.funds.short} />
        <FundBlock fund={snap.funds.long} />
      </div>

      <p className="mt-4 border-t border-edge pt-3 text-[11px] leading-relaxed text-dim">
        Leverage is set inversely to each market's volatility and stop distance proportionally to it, so BTC, ETH and
        SOL each risk roughly the same slice of their own allocation (12.0%, 12.5% and 13.0%). Ladder rungs split a
        market's allocation evenly, so a fully built ladder uses exactly its budget and never more. A market that stops
        out waits {Math.round(snap.funds.short.config.reentryCooldownMs / 60000)} minutes (short) or{' '}
        {Math.round(snap.funds.long.config.reentryCooldownMs / 60000)} minutes (long) before re-entering.
      </p>
    </Panel>
  );
}
